/**
 * Python stock-data-service를 직접 호출하는 유틸리티
 * 로그인하지 않은 경우에도 종가와 배당 정보를 가져올 수 있도록 함
 */

const STOCK_DATA_SERVICE_URL = 'http://localhost:5000';

/** 캐시 TTL: 배당/가격 5분, 히스토리 10분 */
const CACHE_TTL_MS = 5 * 60 * 1000;
const HISTORY_CACHE_TTL_MS = 10 * 60 * 1000;

type CacheEntry<T> = { data: T; expiry: number };

const priceCache = new Map<string, CacheEntry<{ closePrice: number; prevClosePrice: number | null; currency: string; date: string } | null>>();
const dividendCache = new Map<string, CacheEntry<{ annualDividend: number; dividendPerShare: number; dividendDate?: string } | null>>();
const historyCache = new Map<string, CacheEntry<Record<string, number>>>();

function getCached<T>(map: Map<string, CacheEntry<T>>, key: string, ttlMs: number): T | undefined {
  const entry = map.get(key);
  if (!entry) return undefined;
  if (Date.now() > entry.expiry) {
    map.delete(key);
    return undefined;
  }
  return entry.data;
}

function setCached<T>(map: Map<string, CacheEntry<T>>, key: string, data: T, ttlMs: number): void {
  map.set(key, { data, expiry: Date.now() + ttlMs });
}

/**
 * 한국 주식 코드를 Yahoo Finance 티커로 변환
 * @param stockCode 종목코드 (6자리 숫자)
 * @param market 시장 (KOSPI 또는 KOSDAQ, 기본값: KOSPI)
 * @returns Yahoo Finance 티커 (예: 005930.KS)
 */
export function convertToYahooTicker(stockCode: string, market: string = 'KOSPI'): string {
  if (!stockCode) return '';
  
  // 이미 티커 형식인 경우 그대로 반환
  if (stockCode.includes('.')) {
    return stockCode;
  }
  
  // 숫자가 아닌 경우 (미국 주식 등) 그대로 반환
  if (!/^\d+$/.test(stockCode)) {
    return stockCode;
  }
  
  // 숫자인 경우 한국 주식으로 처리
  try {
    // 6자리 숫자로 패딩
    const paddedCode = stockCode.padStart(6, '0');
    
    // KOSPI는 .KS, KOSDAQ은 .KQ
    const suffix = market === 'KOSPI' ? '.KS' : '.KQ';
    
    return paddedCode + suffix;
  } catch (e) {
    console.warn('티커 변환 실패:', stockCode, e);
    return stockCode;
  }
}

/**
 * 종목코드로 종가 정보 조회
 * @param stockCode 종목코드
 * @param ticker Yahoo Finance 티커 (선택사항, 없으면 자동 변환)
 * @returns 종가 정보 { closePrice, currency, date } 또는 null
 */
export async function getStockPrice(
  stockCode: string,
  ticker?: string
): Promise<{ closePrice: number; prevClosePrice: number | null; currency: string; date: string } | null> {
  try {
    const finalTicker = ticker || convertToYahooTicker(stockCode);
    if (!finalTicker) {
      return null;
    }
    const cacheKey = `price:${finalTicker}`;
    const cached = getCached(priceCache, cacheKey, CACHE_TTL_MS);
    if (cached !== undefined) return cached;

    const response = await fetch(`${STOCK_DATA_SERVICE_URL}/api/stock/price?ticker=${encodeURIComponent(finalTicker)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      console.warn(`종가 조회 실패: ${stockCode}, status: ${response.status}`);
      return null;
    }
    
    const data = await response.json();
    
    if (data.error) {
      console.warn(`종가 조회 오류: ${stockCode}, error: ${data.error}`);
      return null;
    }
    
    const result = {
      closePrice: data.closePrice || 0,
      prevClosePrice: data.prevClosePrice ?? null,
      currency: data.currency || 'KRW',
      date: data.priceDate || data.date || new Date().toISOString().split('T')[0],
    };
    setCached(priceCache, cacheKey, result, CACHE_TTL_MS);
    return result;
  } catch (error) {
    console.error(`종가 조회 중 오류 발생: ${stockCode}`, error);
    return null;
  }
}

/**
 * 티커로 배당 정보 조회
 * @param ticker Yahoo Finance 티커
 * @returns 배당 정보 { annualDividend, dividendPerShare, dividendDate } 또는 null
 */
export async function getStockDividend(
  ticker: string
): Promise<{ annualDividend: number; dividendPerShare: number; dividendDate?: string } | null> {
  try {
    if (!ticker) {
      return null;
    }
    const cacheKey = `dividend:${ticker}`;
    const cached = getCached(dividendCache, cacheKey, CACHE_TTL_MS);
    if (cached !== undefined) return cached;

    const response = await fetch(`${STOCK_DATA_SERVICE_URL}/api/stock/dividend?ticker=${encodeURIComponent(ticker)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      console.warn(`배당 조회 실패: ${ticker}, status: ${response.status}`);
      return null;
    }
    
    const data = await response.json();
    
    if (data.error) {
      console.warn(`배당 조회 오류: ${ticker}, error: ${data.error}`);
      return null;
    }
    
    const result = {
      annualDividend: data.annualDividend || 0,
      dividendPerShare: data.dividendPerShare || 0,
      dividendDate: data.dividendDate,
    };
    setCached(dividendCache, cacheKey, result, CACHE_TTL_MS);
    return result;
  } catch (error) {
    console.error(`배당 조회 중 오류 발생: ${ticker}`, error);
    return null;
  }
}

/**
 * 여러 티커의 배당 정보를 일괄 조회 (동일 티커는 1회만 요청, 캐시 사용)
 * @param tickers 티커 배열 (중복 제거 후 조회)
 * @returns 티커별 배당 정보 맵
 */
export async function getStockDividendBatch(
  tickers: string[]
): Promise<Record<string, { annualDividend: number; dividendPerShare: number; dividendDate?: string } | null>> {
  const unique = Array.from(new Set(tickers.filter(Boolean)));
  const results: Record<string, { annualDividend: number; dividendPerShare: number; dividendDate?: string } | null> = {};
  await Promise.all(
    unique.map(async (ticker) => {
      const info = await getStockDividend(ticker);
      results[ticker] = info;
    })
  );
  return results;
}

/**
 * 특정 기간의 종목 일별 종가 히스토리 조회
 * @param ticker Yahoo Finance 티커
 * @param startDate 시작 날짜 (YYYY-MM-DD)
 * @param endDate 종료 날짜 (YYYY-MM-DD)
 * @returns 날짜 → 종가 맵 { 'YYYY-MM-DD': closePrice }
 */
export async function getStockPriceHistory(
  ticker: string,
  startDate: string,
  endDate: string
): Promise<Record<string, number>> {
  try {
    if (!ticker) return {};
    const cacheKey = `history:${ticker}:${startDate}:${endDate}`;
    const cached = getCached(historyCache, cacheKey, HISTORY_CACHE_TTL_MS);
    if (cached !== undefined) return cached;

    const url = `${STOCK_DATA_SERVICE_URL}/api/stock/history?ticker=${encodeURIComponent(ticker)}&start_date=${startDate}&end_date=${endDate}`;
    const response = await fetch(url, { method: 'GET' });
    if (!response.ok) return {};
    const data = await response.json();
    if (!data.prices || !Array.isArray(data.prices)) return {};

    const result: Record<string, number> = {};
    data.prices.forEach((item: { priceDate: string; closePrice: number | null }) => {
      if (item.priceDate && item.closePrice != null) {
        result[item.priceDate] = item.closePrice;
      }
    });
    setCached(historyCache, cacheKey, result, HISTORY_CACHE_TTL_MS);
    return result;
  } catch (error) {
    console.error(`히스토리 조회 오류: ${ticker}`, error);
    return {};
  }
}

/**
 * 여러 종목의 종가 정보를 일괄 조회 (병렬 처리)
 * @param stockCodes 종목코드 배열
 * @param tickers 티커 배열 (선택사항, stockCodes와 같은 순서)
 * @returns 종가 정보 맵 { [stockCode]: { closePrice, currency, date } }
 */
export async function getStockPricesBatch(
  stockCodes: string[],
  tickers?: string[]
): Promise<Record<string, { closePrice: number; prevClosePrice: number | null; currency: string; date: string }>> {
  const results: Record<string, { closePrice: number; prevClosePrice: number | null; currency: string; date: string }> = {};
  
  // 병렬로 조회 (너무 많은 요청을 방지하기 위해 배치로 처리)
  const batchSize = 5; // 한 번에 5개씩 처리
  for (let i = 0; i < stockCodes.length; i += batchSize) {
    const batch = stockCodes.slice(i, i + batchSize);
    const batchTickers = tickers ? tickers.slice(i, i + batchSize) : undefined;
    
    const promises = batch.map((stockCode, index) => {
      const ticker = batchTickers ? batchTickers[index] : undefined;
      return getStockPrice(stockCode, ticker);
    });
    
    const batchResults = await Promise.all(promises);
    
    batch.forEach((stockCode, index) => {
      const priceInfo = batchResults[index];
      if (priceInfo) {
        results[stockCode] = priceInfo;
      }
    });
    
    // Rate limiting 방지를 위한 짧은 지연
    if (i + batchSize < stockCodes.length) {
      await new Promise(resolve => setTimeout(resolve, 200));
    }
  }
  
  return results;
}
